/**
 * `/templates` — the Template library.
 *
 * Transcribed from the mockup's `isTplLib` block.
 *
 * The eyebrow says "intentionally discoverable" because that is a deliberate asymmetry worth
 * stating: Templates are browsable **because they are reusable**, while individual Liturgies never
 * are. Someone arriving from the Liturgies dashboard — which is emphatically an authorized
 * collection and not a search — should understand why this list behaves differently
 * (docs/engineering/01-domain-model.md).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';

import { useAuth } from '../auth/useAuth';

import { duplicateTemplate, fetchTemplate, fetchTemplates, type TemplateSummary } from './api';
import { ImportTemplateModal } from './ImportTemplateModal';
import { slugifyKey } from './bulkOptionParser';
import { createTemplateBundle, downloadFile } from './templateBundle';
import './templates.css';

const VISIBILITY_TONE: Record<string, string> = {
  public: 'tpl-pill--public',
  organization: 'tpl-pill--org',
  private: 'tpl-pill--private',
};

type SortField = 'name' | 'owner' | 'visibility';
type SortDirection = 'asc' | 'desc';

export function TemplateLibraryScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: ['templates'], queryFn: fetchTemplates });
  const { account } = useAuth();

  const [importOpen, setImportOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const duplicate = useMutation({
    mutationFn: (templateId: string) => duplicateTemplate(templateId),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      await navigate({ to: '/templates/$id/edit', params: { id: created.id } });
    },
  });

  const handleExport = async (templateId: string, templateName: string) => {
    try {
      const detail = await fetchTemplate(templateId);
      const bundle = createTemplateBundle(detail);
      const json = JSON.stringify(bundle, null, 2);
      downloadFile(`${slugifyKey(templateName) || 'template'}.pius.json`, json, 'application/json');
    } catch (err) {
      console.error('Failed to export template', err);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const processedTemplates = useMemo(() => {
    if (!templates.data) return [];
    let list = templates.data;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((item) => {
        const nameMatch = item.name.toLowerCase().includes(q);
        const ownerMatch = (item.owner_display ?? '').toLowerCase().includes(q);
        const visMatch = item.visibility.toLowerCase().includes(q);
        const desigMatch = item.designations.some((d) => d.toLowerCase().includes(q));
        const lineageMatch = item.lineage.some((l) => l.toLowerCase().includes(q));
        return nameMatch || ownerMatch || visMatch || desigMatch || lineageMatch;
      });
    }

    return [...list].sort((a, b) => {
      let valA = '';
      let valB = '';
      if (sortField === 'name') {
        valA = a.name.toLowerCase();
        valB = b.name.toLowerCase();
      } else if (sortField === 'owner') {
        valA = (a.owner_display ?? '').toLowerCase();
        valB = (b.owner_display ?? '').toLowerCase();
      } else if (sortField === 'visibility') {
        valA = a.visibility.toLowerCase();
        valB = b.visibility.toLowerCase();
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [templates.data, searchQuery, sortField, sortDirection]);

  return (
    <div className="tpl-lib">
      <header className="tpl-lib__head">
        <div>
          <p className="tpl-eyebrow">{t('templates.reusable_desc')}</p>
          <h1 className="tpl-lib__title">{t('templates.title_library')}</h1>
          <p className="tpl-lib__lede">{t('templates.lede')}</p>
        </div>

        {/* A Template outlives any one celebration and is inherited from by others, so creating one
            needs an Account — the deliberate exception to "accounts are optional", which is about
            planning. The server refuses without one; offering the button anyway would be an
            invitation to a refusal. */}
        {account ? (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              type="button"
              className="tpl-btn tpl-btn--ghost"
              onClick={() => setImportOpen(true)}
            >
              📥 {t('templates.import_template')}
            </button>
            <Link className="tpl-btn tpl-btn--primary" to="/templates/new">
              {t('templates.new_template')}
            </Link>
          </div>
        ) : null}
      </header>

      <div className="tpl-lib__toolbar">
        <div className="tpl-lib__search-wrap">
          <input
            type="search"
            className="tpl-lib__search-input"
            placeholder={t('templates.search_placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery ? (
            <button
              type="button"
              className="tpl-lib__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label={t('templates.clear_search')}
            >
              ✕
            </button>
          ) : null}
        </div>

        {searchQuery ? (
          <span className="tpl-lib__search-count">
            {t(
              processedTemplates.length === 1
                ? 'templates.search_results_count'
                : 'templates.search_results_count_plural',
              { count: processedTemplates.length },
            )}
          </span>
        ) : null}
      </div>

      <div className="tpl-lib__table">
        <div className="tpl-row tpl-row--head">
          <div>
            <button
              type="button"
              className={`tpl-row__head-btn ${sortField === 'name' ? 'tpl-row__head-btn--active' : ''}`}
              onClick={() => handleSort('name')}
              title={t('templates.sort_by', { field: t('templates.col_template') })}
            >
              {t('templates.col_template')}
              <span className="tpl-row__sort-icon">
                {sortField === 'name' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
              </span>
            </button>
          </div>

          <div>
            <button
              type="button"
              className={`tpl-row__head-btn ${sortField === 'owner' ? 'tpl-row__head-btn--active' : ''}`}
              onClick={() => handleSort('owner')}
              title={t('templates.sort_by', { field: t('templates.col_owner') })}
            >
              {t('templates.col_owner')}
              <span className="tpl-row__sort-icon">
                {sortField === 'owner' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
              </span>
            </button>
          </div>

          <div>
            <button
              type="button"
              className={`tpl-row__head-btn ${sortField === 'visibility' ? 'tpl-row__head-btn--active' : ''}`}
              onClick={() => handleSort('visibility')}
              title={t('templates.sort_by', { field: t('templates.col_visibility') })}
            >
              {t('templates.col_visibility')}
              <span className="tpl-row__sort-icon">
                {sortField === 'visibility' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
              </span>
            </button>
          </div>
          <div />
        </div>

        {templates.isPending ? (
          <p className="tpl-lib__state">{t('templates.loading')}</p>
        ) : templates.isError ? (
          <p className="tpl-lib__state">{t('templates.error_loading')}</p>
        ) : templates.data.length === 0 ? (
          <p className="tpl-lib__state">{t('templates.empty_library')}</p>
        ) : processedTemplates.length === 0 ? (
          <p className="tpl-lib__state">
            {t('templates.no_search_results', { query: searchQuery })}
          </p>
        ) : (
          processedTemplates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              canDuplicate={Boolean(account)}
              isDuplicating={duplicate.isPending && duplicate.variables === template.id}
              onDuplicate={(id) => duplicate.mutate(id)}
              onExport={(id, name) => handleExport(id, name)}
            />
          ))
        )}

        <p className="tpl-lib__footnote">{t('templates.footnote')}</p>
      </div>

      <ImportTemplateModal isOpen={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

function TemplateRow({
  template,
  canDuplicate,
  isDuplicating,
  onDuplicate,
  onExport,
}: {
  template: TemplateSummary;
  canDuplicate: boolean;
  isDuplicating: boolean;
  onDuplicate: (id: string) => void;
  onExport: (id: string, name: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');

  const version =
    template.latest_published_version === null
      ? t('templates.no_published_version')
      : `v${template.latest_published_version}`;

  const meta = [version, template.has_draft ? t('templates.draft_in_progress') : null]
    .filter(Boolean)
    .join(' · ');

  const ownerName =
    template.owner_display === 'Account owner' || template.owner_display === 'Account Owner'
      ? t('templates.account_owner')
      : (template.owner_display ?? t('templates.no_account_owner'));

  return (
    <div className={`tpl-row${template.retired_at ? ' tpl-row--retired' : ''}`}>
      <div>
        <Link
          to="/templates/$id/edit"
          params={{ id: template.id }}
          className="tpl-row__name tpl-row__name--link"
        >
          {template.name}
        </Link>
        <div className="tpl-row__meta">{meta}</div>
        {template.lineage.length > 1 ? (
          // The chain reads outward: this template, then what it specializes.
          <div className="tpl-row__lineage">
            {t('templates.specializes')} {template.lineage.slice(1).join(' ← ')}
          </div>
        ) : null}
      </div>

      <div className="tpl-row__owner">{ownerName}</div>

      <div>
        <span className={`tpl-pill ${VISIBILITY_TONE[template.visibility] ?? ''}`}>
          {template.retired_at
            ? t('templates.status_retired')
            : t(`templates.visibility_${template.visibility}`, {
                defaultValue: template.visibility,
              })}
        </span>
        {template.designations.map((d) => (
          <span key={d} className="tpl-pill tpl-pill--designated">
            {t(`templates.designation_${d}`, { defaultValue: d })}
          </span>
        ))}
      </div>

      <div className="tpl-row__action">
        <button
          type="button"
          className="tpl-btn tpl-btn--ghost"
          onClick={() => onExport(template.id, template.name)}
          title={t('templates.export_bundle_tooltip')}
        >
          {t('templates.export')}
        </button>
        <Link
          className="tpl-btn tpl-btn--action"
          to="/templates/$id/edit"
          params={{ id: template.id }}
        >
          {template.can_edit ? t('templates.edit') : t('templates.view')}
        </Link>
        {canDuplicate ? (
          <button
            type="button"
            className="tpl-btn tpl-btn--ghost"
            disabled={isDuplicating}
            onClick={() => onDuplicate(template.id)}
            title={t('templates.duplicate_tooltip')}
          >
            {isDuplicating ? t('templates.duplicating') : t('templates.duplicate')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
